import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { payslipKeys } from './queryKeys';
import { devLog } from '@/utils/logger';

/**
 * Employee-facing payslip realtime invalidation.
 *
 * Payroll generation happens outside this surface, so without a subscription a
 * newly generated payslip only appears after a manual reload.
 */
export function useMyPayslipsRealtime(profileId?: string) {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!profileId) return;

    const invalidate = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        devLog('[useMyPayslipsRealtime] invalidating payslip caches');
        queryClient.invalidateQueries({ queryKey: payslipKeys.all });
      }, 400);
    };

    const channel = supabase
      .channel(`my-payslips-${profileId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payslips', filter: `profile_id=eq.${profileId}` },
        invalidate
      )
      .subscribe();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      supabase.removeChannel(channel);
    };
  }, [profileId, queryClient]);
}
